import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PILLARS, TID, CONSULTATION } from "@/constants/testIds";
import ConsultationForm from "@/components/ConsultationForm";

export default function Pillar() {
  const { slug } = useParams();
  const pillar = PILLARS.find((p) => p.slug === slug);
  if (!pillar) return <Navigate to="/pillars/nature" replace />;

  const idx = PILLARS.findIndex((p) => p.slug === slug);
  const next = PILLARS[(idx + 1) % PILLARS.length];

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[75vh] bg-void overflow-hidden">
        <img
          src={pillar.image}
          alt={pillar.label}
          className="absolute inset-0 w-full h-full object-cover opacity-75"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
        <div className="relative container-editorial px-5 md:px-10 pt-32 md:pt-40 pb-16 md:pb-24">
          <Link to="/pillars/nature" className="eyebrow text-lime inline-flex items-center gap-2 mb-8">
            <ArrowLeft size={14} /> The Five Pillars
          </Link>
          <div className="max-w-3xl fade-up">
            <div className="eyebrow text-cream/70 mb-4">Pillar {idx + 1} of 5</div>
            <h1 className="font-display text-white text-5xl sm:text-6xl md:text-7xl leading-[0.98]">
              {pillar.label}
            </h1>
            <p className="mt-6 font-display italic text-cream/85 text-xl md:text-2xl max-w-2xl leading-snug">
              {pillar.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Editorial body */}
      <section className="section-pad bg-offwhite">
        <div className="container-editorial grid md:grid-cols-12 gap-10">
          <aside className="md:col-span-3">
            <div className="eyebrow text-teal mb-6">
              <span className="editorial-rule" /> The Reading
            </div>
            <div className="space-y-4">
              {pillar.stats.map((s) => (
                <div key={s.label} className="border-l-2 border-forest pl-4">
                  <div className="font-display text-forest text-3xl">{s.value}</div>
                  <div className="eyebrow text-forest/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </aside>
          <div className="md:col-span-8 md:col-start-5 space-y-6">
            <p className="dropcap font-display text-forest text-2xl md:text-[26px] leading-snug italic">
              {pillar.intro}
            </p>
            {pillar.long.map((para, i) => (
              <p key={i} className="text-forest/85 text-base md:text-[17px] leading-relaxed max-w-2xl">
                {para}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links to other pillars */}
      <section className="pb-20 bg-offwhite">
        <div className="container-editorial px-5 md:px-10">
          <div className="eyebrow text-forest/50 mb-6">
            <span className="editorial-rule" /> The Other Pillars
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {PILLARS.filter((p) => p.slug !== slug).map((p) => (
              <Link
                to={`/pillars/${p.slug}`}
                key={p.slug}
                className="pillar-card group h-40 md:h-48"
              >
                <img src={p.image} alt={p.label} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="relative h-full p-4 flex items-end text-cream">
                  <div className="font-display text-xl leading-tight">{p.label}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Inline consultation form */}
      <section className="section-pad bg-forest text-cream">
        <div className="container-editorial grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow text-lime mb-4">
              <span className="editorial-rule bg-lime" /> Online Consultation
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Talk to us about<br />{pillar.label.toLowerCase()}.
            </h2>
            <p className="mt-6 text-cream/75 max-w-md leading-relaxed">
              A free {CONSULTATION.duration}-minute Google Meet with our senior
              sales advisor — added straight to your Google Calendar. We'll go
              deeper on {pillar.label.toLowerCase()} and answer the questions
              your family will ask.
            </p>
            <Link to={`/pillars/${next.slug}`} className="mt-8 inline-flex items-center gap-2 text-lime text-sm">
              Next pillar: {next.label} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="md:col-span-6 md:col-start-7 bg-offwhite p-6 md:p-8 rounded-lg text-forest" data-testid={TID.pillarInlineStrategy}>
            <div className="eyebrow text-teal">Free · 30-min video</div>
            <h3 className="font-display text-forest text-2xl mt-1 mb-4">Book my consultation</h3>
            <ConsultationForm source={`pillar_${slug}`} ctaLabel="Book my consultation" compact />
          </div>
        </div>
      </section>
    </div>
  );
}
